// Sample data for the AI Conversations page demo/empty states.
//
//  DEMO_REVIEW  -> /needs-review  (pendingItems, rendered as <DraftCard/>)
//  DEMO_INBOX   -> /ai-inbox      (convoItems,  rendered as <ConversationRow/>)
//
// Shapes mirror /ai-outbound-queue/list and /ai-conversations/list so the real
// card components render unchanged. While these render, useDemoNetworkGuard
// keeps window.__demoMode on (sends/dismisses resolve as no-op successes and
// remove the card locally) and the page short-circuits its refetch so the
// seeded sample data is never wiped.

import { DEMO_AGENT, DEMO_LEAD_COMPONENTS } from "./demoNeedsReviewData";

const iso = (mins) => new Date(Date.now() - mins * 60000).toISOString();
// LeadScoreBadge's lastEventAt is epoch SECONDS (not an ISO string).
const epoch = (mins) => Math.floor((Date.now() - mins * 60000) / 1000);

const tag = (slug, label, color, source = "ai") => ({
  slug, label, color, category: "status", source,
});

const REVIEW_DRAFTS = [
  {
    id: "demo-draft-1",
    kind: "reply",
    channel: "email",
    contact_key: "e:sarah.mitchell@example.com",
    contact_email: "sarah.mitchell@example.com",
    contact_phone: "0412 345 678",
    contact_tags: [tag("hot-lead", "Hot lead", "#ef4444"), tag("seller", "Seller", "#0d9488", "human")],
    ai_owns_relationship: true,
    draft_version: 1,
    created_at: iso(8),
    lead_score: 92,
    lead_last_event_at: epoch(8),
    trigger_names: ["Replied to your appraisal email"],
    ai_agent_name: "Olivia Bennett",
    user_message_text:
      "Hi, thanks for the appraisal on Darling Street. We're thinking of listing in the next month or two — could you give me a call to talk through timing and your commission?",
    email_subject: "Re: Your property appraisal for 14 Darling Street",
    email_body:
      "Hi Sarah,\n\nGreat to hear you're considering listing — late autumn into winter is still seeing strong buyer demand in Balmain, especially for renovated terraces like yours.\n\nI'd love to talk through timing, recent comparable sales, and how I market homes to get the best result. Are you free for a quick call tomorrow afternoon, or would Saturday morning suit better?\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
    body:
      "Hi Sarah,\n\nGreat to hear you're considering listing — late autumn into winter is still seeing strong buyer demand in Balmain, especially for renovated terraces like yours.\n\nI'd love to talk through timing, recent comparable sales, and how I market homes to get the best result. Are you free for a quick call tomorrow afternoon, or would Saturday morning suit better?\n\nWarm regards,\nOlivia Bennett\nAssociate to John Doe",
  },
  {
    id: "demo-draft-2",
    kind: "outbound",
    channel: "email",
    contact_key: "e:james.nguyen@example.com",
    contact_email: "james.nguyen@example.com",
    contact_phone: "0423 887 210",
    contact_tags: [tag("nurturing", "Nurturing", "#f59e0b")],
    ai_owns_relationship: true,
    draft_version: 1,
    created_at: iso(40),
    lead_score: 68,
    lead_last_event_at: epoch(40),
    trigger_names: ["Viewed a property report 3 times this week"],
    ai_agent_name: "Olivia Bennett",
    email_subject: "A few homes you might like in Hawthorn",
    email_body:
      "Hi James,\n\nI noticed you've been keeping an eye on properties around Glenferrie Road. The market there has picked up over the last few weeks, and a couple of townhouses similar to the ones you viewed have just come up.\n\nWould you like me to send through a shortlist and the latest price guides? Happy to keep you posted as new listings hit the market.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
    body:
      "Hi James,\n\nI noticed you've been keeping an eye on properties around Glenferrie Road. The market there has picked up over the last few weeks, and a couple of townhouses similar to the ones you viewed have just come up.\n\nWould you like me to send through a shortlist and the latest price guides? Happy to keep you posted as new listings hit the market.\n\nCheers,\nOlivia Bennett\nAssociate to John Doe",
  },
  {
    id: "demo-draft-3",
    kind: "outbound",
    channel: "sms",
    contact_key: "e:priya.sharma@example.com",
    contact_email: "priya.sharma@example.com",
    contact_phone: "0438 552 901",
    contact_tags: [tag("buyer", "Buyer", "#3b82f6", "human")],
    ai_owns_relationship: true,
    draft_version: 1,
    created_at: iso(125),
    lead_score: 54,
    lead_last_event_at: epoch(125),
    trigger_names: ["No contact in 30 days"],
    ai_agent_name: "Olivia Bennett",
    sms_body:
      "Hi Priya, it's been a little while! A 2-bed unit in Toowong just listed close to your budget — want me to text you the link and arrange an inspection?",
    body:
      "Hi Priya, it's been a little while! A 2-bed unit in Toowong just listed close to your budget — want me to text you the link and arrange an inspection?",
  },
];

// Attach the shared AI agent persona (a realistic @exampleagency.com sender)
// and each contact's lead-score breakdown so the "From" line and score popover
// render with real-looking data.
export const DEMO_REVIEW = REVIEW_DRAFTS.map((d) => {
  const comp = DEMO_LEAD_COMPONENTS[d.contact_email] || null;
  // LeadScoreBadge expects lastEventAt in epoch SECONDS — derive it from the
  // most recent contributing event so the popover shows "x ago", not "NaN".
  const lastTs = comp
    ? Math.max(...Object.values(comp.events).map((e) => e.last_ts || 0))
    : Math.floor(Date.now() / 1000);
  return {
    ...d,
    ai_agent_name: DEMO_AGENT.name,
    ai_agent_email: DEMO_AGENT.email,
    lead_components: comp,
    lead_last_event_at: lastTs,
  };
});

export const DEMO_INBOX = [
  {
    contact_key: "e:sarah.mitchell@example.com",
    contact_email: "sarah.mitchell@example.com",
    ai_owns_relationship: true,
    last_activity_at: iso(8),
    pending_drafts: 1,
    lead_score: 92,
    lead_last_event_at: epoch(8),
    last_snippet: "Could you give me a call to talk through timing and your commission?",
  },
  {
    contact_key: "e:james.nguyen@example.com",
    contact_email: "james.nguyen@example.com",
    ai_owns_relationship: true,
    last_activity_at: iso(40),
    pending_drafts: 1,
    lead_score: 68,
    lead_last_event_at: epoch(40),
    last_snippet: "AI → A few homes you might like in Hawthorn",
  },
  {
    contact_key: "e:olivia.brown@example.com",
    contact_email: "olivia.brown@example.com",
    ai_owns_relationship: false,
    last_activity_at: iso(180),
    pending_drafts: 0,
    lead_score: 23,
    lead_last_event_at: epoch(180),
    last_snippet: "Thanks for the market update — really helpful, I'll be in touch closer to spring.",
  },
  {
    contact_key: "e:priya.sharma@example.com",
    contact_email: "priya.sharma@example.com",
    ai_owns_relationship: true,
    last_activity_at: iso(1500),
    pending_drafts: 1,
    lead_score: 54,
    lead_last_event_at: epoch(1500),
    last_snippet: "AI → A 2-bed unit in Toowong just listed close to your budget…",
  },
  {
    contact_key: "e:daniel.lee@example.com",
    contact_email: "daniel.lee@example.com",
    ai_owns_relationship: true,
    last_activity_at: iso(4320),
    pending_drafts: 0,
    lead_score: 12,
    lead_last_event_at: epoch(4320),
    last_snippet: "AI → Just checking in — let me know if you'd like an updated estimate on King Street.",
  },
];
